// material-ui
import {
    Box,
    Grid,
    Button,
    Typography,
    TextField,
    Modal,
    CircularProgress,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    IconButton
} from '@mui/material';
import { deepPurple, red } from '@mui/material/colors';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';

import { useState, useEffect } from 'react';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 800,
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const RestrictedCryptoModal = ({
    restrictedModalOpen,
    setRestrictedModalOpen,
    customerRow
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data States
    const [restrictedList, setRestrictedList] = useState([]);
    const [allCryptoSymbols, setAllCryptoSymbols] = useState([]);
    
    // Form State
    const [selectedSymbol, setSelectedSymbol] = useState(null);

    useEffect(() => {
        if (restrictedModalOpen && customerRow?.id) {
            fetchRestrictedCryptos();
            fetchCryptoSymbols();
            setSelectedSymbol(null);
        }
    }, [restrictedModalOpen, customerRow]);

    // 1. Fetch Existing Restrictions
    const fetchRestrictedCryptos = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/CustomerRestrictedCryptoSymbol?customerID=${customerRow.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setRestrictedList(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // 2. Fetch All Available Cryptos
    const fetchCryptoSymbols = async () => {
        try {
            const res = await axiosInstance.get(`/CryptoSymbols/All`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setAllCryptoSymbols(res.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // 3. Add Restriction
    const handleAddRestriction = async () => {
        if (!selectedSymbol) return;

        setIsSubmitting(true);
        try {
            const res = await axiosInstance.post('/CustomerRestrictedCryptoSymbol', {
                customerID: customerRow.id,
                cryptoSymbolID: selectedSymbol.id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200 || res.status === 201) {
                successAlert('Successfully added restriction');
                setSelectedSymbol(null); // Clear input
                fetchRestrictedCryptos(); // Refresh list
            }
        } catch (error) {
            errorAlert('Failed to add restriction');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 4. Delete Restriction
    const handleDeleteRestriction = async (id) => {
        setIsSubmitting(true);
        try {
            const res = await axiosInstance.post(`/CustomerRestrictedCryptoSymbol/Delete/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                successAlert('Successfully removed restriction');
                fetchRestrictedCryptos(); // Refresh list
            }
        } catch (error) {
            errorAlert('Failed to remove restriction');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // UX Improvement: Filter out cryptos that are ALREADY in the restricted list
    const availableCryptosToRestrict = allCryptoSymbols.filter(
        crypto => !restrictedList.some(r => r.cryptoSymbolID === crypto.id)
    );

    return (
        <div>
            {/* Open Modal Button */}
            <Button
                onClick={() => setRestrictedModalOpen(true)}
                sx={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: 'fit-content',
                    height: 'fit-content',
                    color: red[500] // Red icon to signify restriction/blocking
                }}
                title="Manage Restricted Cryptos"
            >
                <BlockIcon />
            </Button>

            {/* Modal */}
            <Modal 
                open={restrictedModalOpen} 
                onClose={() => setRestrictedModalOpen(false)}
            >
                <Box sx={style}>
                    <Typography variant="h4" sx={{ mb: 3 }}>
                        Restricted Cryptos - {customerRow?.username || 'Customer'}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* --- TOP: Add New Restriction --- */}
                            <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                    Restrict a new Crypto Symbol
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={8}>
                                        <Autocomplete
                                            options={availableCryptosToRestrict}
                                            getOptionLabel={(option) => option.code || option.name || ''}
                                            value={selectedSymbol}
                                            onChange={(event, newValue) => setSelectedSymbol(newValue)}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    label="Select Crypto Symbol" 
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={handleAddRestriction}
                                            disabled={isSubmitting || !selectedSymbol}
                                        >
                                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Add Restriction"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* --- BOTTOM: Existing Restrictions Table --- */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Currently Restricted Symbols
                            </Typography>
                            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>ID</b></TableCell>
                                            <TableCell><b>Crypto Symbol</b></TableCell>
                                            <TableCell align="center"><b>Action</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {restrictedList.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                                    No restrictions found for this customer.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            restrictedList.map((row) => (
                                                <TableRow key={row.id} hover>
                                                    <TableCell>{row.id}</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{row.cryptoSymbolName}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton 
                                                            color="error" 
                                                            onClick={() => handleDeleteRestriction(row.id)}
                                                            disabled={isSubmitting}
                                                            title="Remove Restriction"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                <Button variant="outlined" onClick={() => setRestrictedModalOpen(false)}>
                                    Close
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default RestrictedCryptoModal;